type GroupKey =
  | 'SN' // Strictly Necessary
  | 'P' // Performance
  | 'F' // Functional
  | 'T' // Tracking/Targeting
  | 'UNKNOWN';

// eslint-disable-next-line no-unused-vars
type UpdateCallBackFunction = (newKeys: GroupKey[], oldKeys: GroupKey[]) => void;

/* The OneTrust object is something we don't want to expose to the rest of the app */
type OneTrust = {
  ToggleInfoDisplay(): void;
};

class OneTrustHelper {
  private groupKeys: GroupKey[];

  private readonly callbacks: UpdateCallBackFunction[];

  public readonly oneTrustReady: Promise<OneTrust>;

  constructor() {
    this.groupKeys = [];
    this.callbacks = [];

    // wait for the OneTrust object to be ready
    this.oneTrustReady = new Promise<OneTrust>((res) => {
      const interval = setInterval(() => {
        if (!window.OneTrust) {
          return;
        }
        clearInterval(interval);
        res(window.OneTrust);
      }, 300);
      setTimeout(() => clearInterval(interval), 20000);
    });
  }

  public updateGroupKeys() {
    const oldKeys = this.groupKeys;
    try {
      this.innerUpdateGroupKeys();
    } catch (e) {
      console.error(`OneTrustHelperError - Could not update group cookies ${e}`);
    }
    const newKeys = this.groupKeys;
    this.callbacks.forEach((cb) => cb(newKeys, oldKeys));
  }

  public setUpdateGroupKeysCallback(cb: UpdateCallBackFunction) {
    this.callbacks.push(cb);
  }

  private innerUpdateGroupKeys() {
    const cookie = document.cookie.split(';').find((cookie) => {
      return cookie.trim().startsWith('OptanonConsent=');
    });

    if (!cookie) {
      console.warn('OneTrustHelper - No OptanonConsent cookie found');
      this.groupKeys = [];
      return;
    }

    const u = new URLSearchParams(cookie);
    const vars = u.get('groups')?.split(',') || [];
    // group values are like "C0002:1" (:1 == accept, :0 reject)
    this.groupKeys = vars
      .map((vv) => vv.split(':'))
      .filter(([, accepted]) => accepted === '1')
      .map(([id]) => {
        switch (id) {
          case 'C0001':
            return 'SN';
          case 'C0002':
            return 'P';
          case 'C0003':
            return 'F';
          case 'C0004':
            return 'T';
          default:
            return 'UNKNOWN';
        }
      });
  }

  /** Return true if consented to all keys */
  public isConsent(keys: GroupKey[]): boolean {
    try {
      return keys.filter((key) => !this.groupKeys.includes(key)).length === 0;
    } catch (e) {
      console.error(`OneTrustHelperError - isConsent error ${e}`);
      return false;
    }
  }
}
export default defineNuxtPlugin(() => {
  if (import.meta.server || window.oneTrustHelper) return;
  const oneTrustHelper = new OneTrustHelper();
  window.oneTrustHelper = oneTrustHelper;
  oneTrustHelper.updateGroupKeys();
});
